/**
 * The KPI table: Name (a bell when an alert is set), Client, Metric, Current
 * Value (the alert interval's value over the target period's), Target with a
 * progress bar coloured by pace, and the row menu.
 */

import { type ReactNode } from 'react';

import { formatValue } from '../reports/format.ts';
import type { Catalogue, CatalogueMetric } from '../reports/reports-api.ts';
import {
  conditionText,
  paceText,
  periodText,
  progressShare,
  STATUS_LABEL,
  statusTone,
} from './kpi-format.ts';
import { BellIcon } from './BellIcon.tsx';
import { KpiRowMenu } from './KpiRowMenu.tsx';
import type { Kpi } from './kpis-api.ts';

const PERCENT = 100;

function metricOf(
  catalogue: Catalogue,
  kpi: Kpi,
): { source: string; metric: CatalogueMetric | undefined } {
  const source = catalogue.sources.find((item) => item.key === kpi.source);
  return {
    source: source?.label ?? kpi.source,
    metric: source?.metrics.find((item) => item.key === kpi.metric),
  };
}

function TargetCell({ kpi, metric }: { kpi: Kpi; metric: CatalogueMetric | undefined }): ReactNode {
  if (!kpi.target_type) return <span className="kp-faint">No target</span>;
  const tone = statusTone(kpi.last_status);
  const share = progressShare(kpi);
  const pace = paceText(kpi);
  return (
    <div className="kp-target">
      <span className="kp-target-text">
        {conditionText(kpi.target_condition, kpi.target_value, metric)}
        {kpi.last_target !== null && kpi.target_condition?.endsWith('_BY') && (
          <small> ({formatValue(kpi.last_target, metric?.format ?? 'number')})</small>
        )}
      </span>
      <span
        className={`kp-bar kp-bar-${tone}`}
        role="progressbar"
        aria-label={`${kpi.title} progress`}
        aria-valuemin={0}
        aria-valuemax={PERCENT}
        aria-valuenow={Math.round(share * PERCENT)}
      >
        <span style={{ width: `${share * PERCENT}%` }} />
      </span>
      <small className={`kp-pace kp-pace-${tone}`}>
        {Math.round(share * PERCENT)}% ·{' '}
        {kpi.last_status ? STATUS_LABEL[kpi.last_status] : 'Not refreshed yet'}
        {pace && kpi.last_status !== 'MET' && kpi.last_status !== 'MISSED' && ` · ${pace}`}
      </small>
    </div>
  );
}

function CurrentCell({
  kpi,
  metric,
}: {
  kpi: Kpi;
  metric: CatalogueMetric | undefined;
}): ReactNode {
  const format = metric?.format ?? 'number';
  if (kpi.last_status === 'ERROR')
    return (
      <span className="kp-error" title={kpi.last_error ?? ''}>
        Could not refresh
      </span>
    );
  return (
    <div className="kp-current">
      {kpi.alert_condition && (
        <span title="This alert interval so far">{formatValue(kpi.last_alert_value, format)}</span>
      )}
      <strong title="The target period so far">{formatValue(kpi.last_value, format)}</strong>
      <small>{periodText(kpi)}</small>
    </div>
  );
}

export function KpiTable(props: {
  organisationId: string;
  catalogue: Catalogue;
  kpis: Kpi[];
  total: number;
  showClient: boolean;
  onEdit: (kpi: Kpi) => void;
  onChanged: (kpi: Kpi) => void;
  onRemoved: (id: string) => void;
  onProblem: (problem: string | null) => void;
}): ReactNode {
  return (
    <div className="ag-table-card">
      <p className="ag-table-meta">
        Showing {props.kpis.length} of {props.total} Rows
      </p>
      <table className="ag-table kp-table">
        <thead>
          <tr>
            <th scope="col">Name</th>
            {props.showClient && <th scope="col">Client</th>}
            <th scope="col">Metric</th>
            <th scope="col">Current Value</th>
            <th scope="col">Target</th>
            <th scope="col">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {props.kpis.map((kpi) => {
            const { source, metric } = metricOf(props.catalogue, kpi);
            return (
              <tr key={kpi.id}>
                <td>
                  <button type="button" className="kp-name" onClick={() => props.onEdit(kpi)}>
                    {kpi.title}
                  </button>
                  {kpi.alert_condition && (
                    <span className="kp-bell" title="Has an alert">
                      <BellIcon size={14} />
                      <span className="sr-only">Has an alert</span>
                    </span>
                  )}
                </td>
                {props.showClient && <td>{kpi.client_name}</td>}
                <td>
                  {metric?.label ?? kpi.metric}
                  <small className="kp-faint kp-block">{source}</small>
                </td>
                <td>
                  <CurrentCell kpi={kpi} metric={metric} />
                </td>
                <td>
                  <TargetCell kpi={kpi} metric={metric} />
                </td>
                <td className="kp-actions">
                  <KpiRowMenu
                    organisationId={props.organisationId}
                    catalogue={props.catalogue}
                    kpi={kpi}
                    onEdit={() => props.onEdit(kpi)}
                    onChanged={props.onChanged}
                    onRemoved={props.onRemoved}
                    onProblem={props.onProblem}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
